import { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import type { PluginListenerHandle } from "@capacitor/core";

/**
 * Custom hook to handle keyboard visibility
 * @returns {boolean} isKeyboardVisible - True if keyboard is showing, false otherwise
 */
export function useKeyboardHandling() {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  useEffect(() => {
    const configureKeyboard = async () => {
      if (Capacitor.getPlatform() === "web") return;
      try {
        await Keyboard.setResizeMode({
          mode: KeyboardResize.Native
        });
        await Keyboard.setScroll({ isDisabled: true });
      } catch (error) {
        // console.error("Keyboard config failed:", error);
      }
    };

    configureKeyboard();

    const pluginListeners: (PluginListenerHandle | null)[] = [];
    let windowListenersAdded = false;

    const handleShow = () => {
      setIsKeyboardVisible(true);
    };

    const handleHide = () => {
      setIsKeyboardVisible(false);
    };

    const setupListeners = async () => {
      if (Capacitor.getPlatform() === "web") return;

      try {
        pluginListeners.push(await Keyboard.addListener("keyboardWillShow", handleShow));
        pluginListeners.push(await Keyboard.addListener("keyboardDidShow", handleShow));
        pluginListeners.push(await Keyboard.addListener("keyboardWillHide", handleHide));
        pluginListeners.push(await Keyboard.addListener("keyboardDidHide", handleHide));

        if (!windowListenersAdded) {
          window.addEventListener("keyboardWillShow", handleShow as EventListener);
          window.addEventListener("keyboardDidShow", handleShow as EventListener);
          window.addEventListener("keyboardWillHide", handleHide as EventListener);
          window.addEventListener("keyboardDidHide", handleHide as EventListener);
          windowListenersAdded = true;
        }
      } catch (error) {
        // console.error("Keyboard listener setup failed:", error);
      }
    };

    setupListeners();

    return () => {
      pluginListeners.forEach((listener) => listener?.remove());
      if (windowListenersAdded) {
        window.removeEventListener("keyboardWillShow", handleShow as EventListener);
        window.removeEventListener("keyboardDidShow", handleShow as EventListener);
        window.removeEventListener("keyboardWillHide", handleHide as EventListener);
        window.removeEventListener("keyboardDidHide", handleHide as EventListener);
      }
    };
  }, []);

  return isKeyboardVisible;
}

export default useKeyboardHandling;
